import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Search, FileText, Download, RefreshCw, AlertCircle, Mail, Globe2, X as XIcon,
} from 'lucide-react';
import { apiService, IntakeListItem } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import PageHeader from './ui/PageHeader';
import StatusPill, { StatusTone } from './ui/StatusPill';
import EmptyState from './ui/EmptyState';

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const fmtDateTime = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
};

const fmtDateDe = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const MAIL_STATUS_META: Record<string, { label: string; tone: StatusTone }> = {
    sent: { label: 'Versendet', tone: 'success' },
    failed: { label: 'Fehlgeschlagen', tone: 'danger' },
    pending: { label: 'Ausstehend', tone: 'neutral' },
};

const LANG_META: Record<string, string> = { de: 'Deutsch', en: 'English', es: 'Español', ru: 'Русский' };

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
    <th
        scope="col"
        className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
        {children}
    </th>
);

interface StatCardProps {
    label: string;
    value: number;
    tone: 'neutral' | 'success' | 'warning' | 'danger';
    icon?: React.ReactNode;
}
const StatCard: React.FC<StatCardProps> = ({ label, value, tone, icon }) => {
    const toneClass = {
        neutral: 'text-brand-text',
        success: 'text-emerald-600',
        warning: 'text-amber-600',
        danger: 'text-rose-600',
    }[tone];
    return (
        <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wider text-brand-text-muted">{label}</span>
                {icon && <span className="text-brand-text-muted">{icon}</span>}
            </div>
            <div className={`mt-2 text-2xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
        </div>
    );
};

const IntakeListPage: React.FC = () => {
    const { addToast } = useToast();
    const [forms, setForms] = useState<IntakeListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [resendingId, setResendingId] = useState<number | null>(null);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const [search, setSearch] = useState('');
    const [mailStatusFilter, setMailStatusFilter] = useState<string>('');
    const [intakeStatusFilter, setIntakeStatusFilter] = useState<string>('');
    const [langFilter, setLangFilter] = useState<string>('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await apiService.listIntakeForms();
            setForms(res.data.forms || []);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Bögen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        return forms.filter((f) => {
            if (search) {
                const q = search.toLowerCase();
                const hit = `${f.patient_name} ${f.patient_email} ${f.signed_place}`.toLowerCase().includes(q);
                if (!hit) return false;
            }
            if (mailStatusFilter && f.mail_status !== mailStatusFilter) return false;
            if (intakeStatusFilter && f.intake_status !== intakeStatusFilter) return false;
            if (langFilter && f.language !== langFilter) return false;
            if (from && (f.submitted_at || '') < from) return false;
            if (to && (f.submitted_at || '') > `${to}T23:59:59Z`) return false;
            return true;
        });
    }, [forms, search, mailStatusFilter, intakeStatusFilter, langFilter, from, to]);

    const resetFilters = () => {
        setSearch(''); setMailStatusFilter(''); setIntakeStatusFilter('');
        setLangFilter(''); setFrom(''); setTo('');
    };
    const hasActiveFilters = !!(search || mailStatusFilter || intakeStatusFilter || langFilter || from || to);

    // Counts for the stat cards (over the unfiltered set so they reflect the whole
    // archive — filters narrow the table, not the global numbers).
    const stats = useMemo(() => ({
        total: forms.length,
        sent: forms.filter((f) => f.mail_status === 'sent').length,
        failed: forms.filter((f) => f.mail_status === 'failed').length,
        pending_mail: forms.filter((f) => f.mail_status === 'pending').length,
    }), [forms]);

    const failedIds = useMemo(() => filtered.filter((f) => f.mail_status === 'failed').map((f) => f.appointment_id), [filtered]);
    const [bulkResending, setBulkResending] = useState(false);

    const handleDownload = async (item: IntakeListItem) => {
        try {
            setDownloadingId(item.appointment_id);
            const blob = await apiService.fetchIntakePdf(item.appointment_id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const safe = item.patient_name.replace(/[^a-zA-Z0-9]+/g, '_');
            a.download = `intake-${item.appointment_id}-${safe}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'PDF konnte nicht geladen werden', 'error');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleResend = async (item: IntakeListItem) => {
        try {
            setResendingId(item.appointment_id);
            const res = await apiService.resendIntakeMail(item.appointment_id);
            if (res.data.success) {
                addToast(`Mail an ${res.data.mail_to} versendet`, 'success');
            } else {
                addToast(`Versand fehlgeschlagen: ${res.data.error || 'Unbekannt'}`, 'error');
            }
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Resend fehlgeschlagen', 'error');
        } finally {
            setResendingId(null);
        }
    };

    const handleBulkResend = async () => {
        if (failedIds.length === 0) return;
        if (!window.confirm(`Mail-Versand für ${failedIds.length} fehlgeschlagene Bögen erneut versuchen?`)) return;
        try {
            setBulkResending(true);
            const results = await Promise.all(failedIds.map((id) => apiService.resendIntakeMail(id).catch((e) => ({
                data: { success: false, error: e?.message } as any,
            }))));
            const ok = results.filter((r) => r.data?.success).length;
            const fail = results.length - ok;
            addToast(`Bulk-Resend: ${ok} erfolgreich, ${fail} weiterhin fehlgeschlagen`, ok > 0 ? 'success' : 'error');
            await load();
        } finally {
            setBulkResending(false);
        }
    };

    return (
        <div>
            <PageHeader
                eyebrow="Patientenakte"
                title="Erhebungsbögen"
                description="Übersicht aller eingereichten Patientenbögen mit Versand-Status und PDF-Archiv"
                actions={
                    <span className="text-xs text-brand-text-muted">
                        <span className="font-semibold text-brand-text tabular-nums">{filtered.length}</span> von{' '}
                        <span className="tabular-nums">{forms.length}</span>
                    </span>
                }
            />

            {/* Stats */}
            <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Bögen gesamt" value={stats.total} tone="neutral" icon={<FileText className="h-4 w-4" strokeWidth={1.75} />} />
                <StatCard label="Mail versendet" value={stats.sent} tone="success" icon={<Mail className="h-4 w-4" strokeWidth={1.75} />} />
                <StatCard label="Mail fehlgeschlagen" value={stats.failed} tone="danger" icon={<AlertCircle className="h-4 w-4" strokeWidth={1.75} />} />
                <StatCard label="Mail ausstehend" value={stats.pending_mail} tone="warning" icon={<Mail className="h-4 w-4" strokeWidth={1.75} />} />
            </div>

            {error && (
                <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
                    {error}
                </div>
            )}

            {/* Filters */}
            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="lg:col-span-2">
                        <label className={labelClass}>Suche (Name, E-Mail, Ort)</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none" strokeWidth={1.75} aria-hidden />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="z. B. Müller oder Palma"
                                className={`${inputClass} pl-9`}
                            />
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>Bogen-Status</label>
                        <select value={intakeStatusFilter} onChange={(e) => setIntakeStatusFilter(e.target.value)} className={inputClass}>
                            <option value="">Alle</option>
                            <option value="submitted">Ausgefüllt</option>
                            <option value="pending">Wartet auf Patient</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Mail-Status</label>
                        <select value={mailStatusFilter} onChange={(e) => setMailStatusFilter(e.target.value)} className={inputClass}>
                            <option value="">Alle</option>
                            {Object.entries(MAIL_STATUS_META).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Sprache</label>
                        <select value={langFilter} onChange={(e) => setLangFilter(e.target.value)} className={inputClass}>
                            <option value="">Alle</option>
                            {Object.entries(LANG_META).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Eingereicht ab</label>
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Eingereicht bis</label>
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass} />
                    </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        {failedIds.length > 0 && (
                            <button
                                onClick={handleBulkResend}
                                disabled={bulkResending}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#ff6f59] hover:bg-[#e55a45] rounded-md transition-colors disabled:opacity-50 cursor-pointer"
                            >
                                <RefreshCw className={`h-3.5 w-3.5 ${bulkResending ? 'animate-spin' : ''}`} strokeWidth={2} />
                                {bulkResending ? 'Sende …' : `Alle fehlgeschlagenen erneut senden (${failedIds.length})`}
                            </button>
                        )}
                    </div>
                    {hasActiveFilters && (
                        <button
                            onClick={resetFilters}
                            className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 rounded-sm px-1"
                        >
                            <XIcon className="h-3 w-3" strokeWidth={2} />
                            Filter zurücksetzen
                        </button>
                    )}
                </div>
            </div>

            {/* List */}
            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sm text-brand-text-muted">Lade…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={FileText}
                        title={hasActiveFilters ? 'Keine Treffer' : 'Noch keine Bögen'}
                        description={
                            hasActiveFilters
                                ? 'Keine Erhebungsbögen entsprechen den aktuellen Filtern.'
                                : 'Sobald Patienten den Bogen ausfüllen, erscheinen sie hier.'
                        }
                    />
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-brand-border">
                                <thead className="bg-brand-surface-2/60">
                                    <tr>
                                        <Th>Patient</Th>
                                        <Th>Termin</Th>
                                        <Th>Eingereicht</Th>
                                        <Th>Sprache</Th>
                                        <Th>Mail-Status</Th>
                                        <Th align="right">Aktionen</Th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-brand-border">
                                    {filtered.map((f) => {
                                        const mailMeta = MAIL_STATUS_META[f.mail_status] || MAIL_STATUS_META.pending;
                                        return (
                                            <tr key={f.id} className="hover:bg-brand-surface-2/60 transition-colors">
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="font-medium text-brand-text">{f.patient_name}</div>
                                                    <div className="text-xs text-brand-text-muted truncate max-w-[200px]">{f.patient_email}</div>
                                                </td>
                                                <td className="px-4 py-3 text-sm whitespace-nowrap">
                                                    <div className="font-medium text-brand-text tabular-nums">{fmtDateDe(f.appointment_date)}</div>
                                                    <div className="text-xs text-brand-text-muted tabular-nums">{f.start_time}</div>
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="text-brand-text">{fmtDateTime(f.submitted_at)}</div>
                                                    <div className="text-xs text-brand-text-muted">{f.signed_place || '—'}</div>
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-text-muted">
                                                        <Globe2 className="h-3 w-3" strokeWidth={1.75} />
                                                        {f.language.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <StatusPill tone={mailMeta.tone} dot>{mailMeta.label}</StatusPill>
                                                    {f.mail_status === 'failed' && f.mail_last_error && (
                                                        <div className="mt-1 text-[10px] text-rose-700 max-w-[240px] truncate" title={f.mail_last_error}>
                                                            {f.mail_last_error}
                                                        </div>
                                                    )}
                                                    {f.mail_status === 'sent' && f.mail_last_at && (
                                                        <div className="mt-1 text-[10px] text-brand-text-muted">{fmtDateTime(f.mail_last_at)}</div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        {f.pdf_available && (
                                                            <button
                                                                onClick={() => handleDownload(f)}
                                                                disabled={downloadingId === f.appointment_id}
                                                                title="PDF herunterladen"
                                                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-text hover:bg-brand-surface-2 rounded transition-colors disabled:opacity-50 cursor-pointer"
                                                            >
                                                                <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                                <span className="hidden lg:inline">PDF</span>
                                                            </button>
                                                        )}
                                                        {f.mail_status === 'failed' && (
                                                            <button
                                                                onClick={() => handleResend(f)}
                                                                disabled={resendingId === f.appointment_id}
                                                                title="Mail erneut senden"
                                                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-white bg-[#ff6f59] hover:bg-[#e55a45] rounded transition-colors disabled:opacity-50 cursor-pointer"
                                                            >
                                                                <RefreshCw className={`h-3.5 w-3.5 ${resendingId === f.appointment_id ? 'animate-spin' : ''}`} strokeWidth={1.75} />
                                                                <span className="hidden lg:inline">Senden</span>
                                                            </button>
                                                        )}
                                                        <Link
                                                            to={`/admin/appointments`}
                                                            state={{ openAppointmentId: f.appointment_id }}
                                                            title="Zum Termin"
                                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-primary hover:bg-brand-accent/10 rounded transition-colors cursor-pointer"
                                                        >
                                                            <span className="hidden lg:inline">Termin</span>
                                                            <span className="lg:hidden">→</span>
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards */}
                        <ul className="md:hidden divide-y divide-brand-border">
                            {filtered.map((f) => {
                                const mailMeta = MAIL_STATUS_META[f.mail_status] || MAIL_STATUS_META.pending;
                                return (
                                    <li key={f.id} className="p-4 space-y-2">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="font-medium text-brand-text truncate">{f.patient_name}</div>
                                                <div className="text-xs text-brand-text-muted truncate">{f.patient_email}</div>
                                            </div>
                                            <StatusPill tone={mailMeta.tone} dot>{mailMeta.label}</StatusPill>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-brand-text-muted">
                                            <span>Termin {fmtDateDe(f.appointment_date)} · {f.start_time}</span>
                                            <span className="uppercase">{f.language}</span>
                                        </div>
                                        <div className="text-xs text-brand-text-muted">
                                            Eingereicht: {fmtDateTime(f.submitted_at)}
                                        </div>
                                        {f.mail_status === 'failed' && f.mail_last_error && (
                                            <div className="text-[10px] text-rose-700">{f.mail_last_error}</div>
                                        )}
                                        <div className="flex gap-2 pt-1">
                                            {f.pdf_available && (
                                                <button
                                                    onClick={() => handleDownload(f)}
                                                    disabled={downloadingId === f.appointment_id}
                                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-brand-text bg-brand-surface-2 rounded-md disabled:opacity-50 cursor-pointer"
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                    PDF
                                                </button>
                                            )}
                                            {f.mail_status === 'failed' && (
                                                <button
                                                    onClick={() => handleResend(f)}
                                                    disabled={resendingId === f.appointment_id}
                                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-[#ff6f59] rounded-md disabled:opacity-50 cursor-pointer"
                                                >
                                                    <RefreshCw className={`h-3.5 w-3.5 ${resendingId === f.appointment_id ? 'animate-spin' : ''}`} />
                                                    Erneut senden
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
            </div>
        </div>
    );
};

export default IntakeListPage;
